import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { API_BASE_URL } from '../../types';
import { PRODUCTS_BY_ID } from '../../constants/products';
import { useAuthUser } from '../../providers/AuthProvider';

export type ProductAssetAudio = {
  id: number;
  name: string;
  size: string | null;
  url: string;
  video_id: number;
  lang: string | null;
  type: string | null;
  user_id?: number | null;
  create_time: string | null;
  update_time: string | null;
};

export type ProductAssetVideo = {
  id: number;
  name: string;
  product_id: number;
  type: string | null;
  category: string | null;
  url: string;
  version: number | null;
  duration: number | null;
  size?: string | null;
  create_time: string | null;
  update_time: string | null;
  audios: ProductAssetAudio[];
};

type ProductAssetResponse = {
  data: {
    product: {
      id: number;
      name: string;
      slug: string | null;
      short_description: string | null;
      image: string | null;
      category: string | null;
      sub_category: string | null;
      lang: string | null;
      is_enabled: boolean;
      definition?: {
        id: number;
        name: string;
        category: string | null;
      };
    };
    videos: ProductAssetVideo[];
  };
};

export type OptionWithIcon<TMeta = undefined> = {
  value: string;
  label: string;
  subtitle?: string;
  icon?: string;
  meta?: TMeta;
};

export type ProductAssetsResult = {
  product: ProductAssetResponse['data']['product'];
  videoOptions: OptionWithIcon<ProductAssetVideo>[];
  audioOptionsByVideoId: Record<number, OptionWithIcon<ProductAssetAudio>[]>;
  videos: ProductAssetVideo[];
};

type ProductDefinition = (typeof PRODUCTS_BY_ID)[number] & {
  image?: string | null;
};

const productAssetsQueryKey = (productId: number, userId?: number | null) =>
  ['products', 'assets', productId, userId ?? 'anon'] as const;

const buildVideoSubtitle = (video: ProductAssetVideo) => {
  const parts: string[] = [];
  if (video.category) {
    parts.push(video.category.toUpperCase());
  }
  if (video.duration) {
    const minutes = Math.floor(video.duration / 60);
    const seconds = Math.floor(video.duration % 60);
    parts.push(`${minutes}m ${seconds}s`);
  }
  return parts.join(' · ') || undefined;
};

const buildAudioSubtitle = (audio: ProductAssetAudio) => {
  const parts: string[] = [];
  if (audio.lang) {
    parts.push(audio.lang.toUpperCase());
  }
  if (audio.type) {
    parts.push(audio.type);
  }
  return parts.join(' · ') || undefined;
};

const fetchProductAssets = async (
  productId: number
): Promise<ProductAssetsResult> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/product/${productId}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch product assets: ${response.status}`);
  }

  const payload: ProductAssetResponse = await response.json();
  const { product, videos = [] } = payload.data ?? {
    product: null,
    videos: [],
  };

  if (!product) {
    throw new Error(`Product ${productId} not found`);
  }

  const productDefinition = PRODUCTS_BY_ID[product.id] as
    | ProductDefinition
    | undefined;
  const assetIcon = product.image ?? productDefinition?.image ?? undefined;
  const filteredVideos = videos.filter(
    video => video.category?.toLowerCase() === '3d'
  );

  const videoOptions = filteredVideos.map<OptionWithIcon<ProductAssetVideo>>(
    video => ({
      value: String(video.id),
      label: video.name,
      subtitle: buildVideoSubtitle(video),
      icon: assetIcon,
      meta: video,
    })
  );

  const audioOptionsByVideoId = filteredVideos.reduce<
    Record<number, OptionWithIcon<ProductAssetAudio>[]>
  >((accumulator, video) => {
    accumulator[video.id] = (video.audios ?? []).map(
      (audio): OptionWithIcon<ProductAssetAudio> => ({
        value: String(audio.id),
        label: audio.name,
        subtitle: buildAudioSubtitle(audio),
        icon: assetIcon,
        meta: audio,
      })
    );
    return accumulator;
  }, {});

  return {
    product,
    videoOptions,
    audioOptionsByVideoId,
    videos: filteredVideos,
  };
};

export function useProductAssetsQuery(
  productId: number | null | undefined,
  options?: UseQueryOptions<ProductAssetsResult, Error>
) {
  const user = useAuthUser();
  const enabled = typeof productId === 'number' && productId > 0;
  const safeQueryKey = productId
    ? productAssetsQueryKey(productId, user?.id)
    : (['products', 'assets', 'unknown'] as const);

  return useQuery<ProductAssetsResult, Error>({
    queryKey: safeQueryKey,
    queryFn: async () => {
      if (!enabled) {
        throw new Error(
          'A valid productId is required to fetch product assets'
        );
      }
      return fetchProductAssets(productId as number);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export { productAssetsQueryKey, fetchProductAssets };
